import { Disclaimer } from '@stodge-inc/block-rendering';
import { api } from 'controllers/network/apiClient';
import { DisclaimerLanguage } from '../constants/disclaimer';

const DISCLAIMER_BASE_URL = '/sdk/config';

/**
 * Server-resolved localized disclaimer copy for a single non-default language.
 * Sourced from the same DB-backed content the storefront renders, so the editor
 * preview matches the live popup without duplicating any copy on the FE.
 *
 * `terms_of_service_url` / `privacy_policy_url` are present only when the shop
 * has configured them for this language (shop-level Compliance settings). When
 * absent, the corresponding link renders as plain label text.
 */
export type LocalizedDisclaimerContent = {
  text: string;
  terms_of_service_label: string;
  privacy_policy_label: string;
  terms_of_service_url?: string;
  privacy_policy_url?: string;
  business_info_label?: string;
  business_info_url?: string;
};

/**
 * The `/sdk/config` `disclaimer` payload. Extends the SDK `Disclaimer` (the EN
 * copy + merchant links) with a `localized` map keyed by non-EN language. The
 * map is optional: older API responses (or shops without translated content)
 * omit it, in which case callers fall back to the EN disclaimer.
 */
export type DisclaimerWithLocalized = Disclaimer & {
  localized?: Partial<
    Record<Exclude<DisclaimerLanguage, 'EN'>, LocalizedDisclaimerContent>
  >;
};

export const getDisclaimerText = async (
  shopId: number,
): Promise<DisclaimerWithLocalized> => {
  const result = await api.get(`${DISCLAIMER_BASE_URL}?shop_id=${shopId}`);
  const disclaimer = result?.data?.disclaimer;

  if (!disclaimer) {
    throw new Error('Disclaimer data not found');
  }

  return disclaimer;
};
